/**
 * 생년월일 · 태어난 시각 고르기 시트의 틀 (귀인도감 client/src/components/pickers/PickerSheet.jsx 자리, D78 · D79).
 * 작은 머리글(양력 · TIME) + 제목 + 닫기, 본문, 아래 확인 버튼 + 「취소」.
 * 사주 입력 하단 시트(BirthSheet) 위에 뜨므로 따로 겹(z-70)을 쓰고, Esc 는 이 시트만 닫는다.
 */
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Button } from '../Button.jsx';

export function PickerDialog({
    eyebrow,
    title,
    ariaLabel,
    confirmLabel,
    confirmDisabled = false,
    onConfirm,
    onClose,
    children,
}) {
    const panelRef = useRef(null);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    useEffect(() => {
        panelRef.current?.focus();
        // 아래 하단 시트(BirthSheet)까지 같이 닫히지 않게 이 시트에서 Esc 를 먼저 받음
        function handleKeyDown(event) {
            if (event.key === 'Escape') {
                event.stopImmediatePropagation();
                event.preventDefault();
                onCloseRef.current();
            }
        }
        document.addEventListener('keydown', handleKeyDown, true);
        return () => document.removeEventListener('keydown', handleKeyDown, true);
    }, []);

    return createPortal(
        <div className="fixed inset-0 z-[70] flex items-end justify-center sm:items-center" role="presentation">
            <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />
            <section
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-label={ariaLabel}
                tabIndex={-1}
                className="relative z-10 m-3 flex max-h-[92dvh] w-full max-w-[420px] flex-col gap-4 overflow-y-auto rounded-[20px] bg-surface p-5 outline-none motion-safe:animate-message-in"
            >
                <div className="flex items-start justify-between">
                    <div className="flex flex-col">
                        <span className="text-[12px] font-semibold tracking-[0.12em] text-text-secondary">
                            {eyebrow}
                        </span>
                        <h2 className="m-0 text-[20px] leading-7 font-bold">{title}</h2>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="닫기"
                        className="flex size-10 items-center justify-center rounded-full"
                    >
                        <X size={22} strokeWidth={1.8} aria-hidden="true" />
                    </button>
                </div>
                {children}
                <div className="flex flex-col gap-1">
                    <Button size="lg" fullWidth disabled={confirmDisabled} onClick={onConfirm}>
                        {confirmLabel}
                    </Button>
                    <button
                        type="button"
                        onClick={onClose}
                        className="h-11 text-[15px] font-semibold text-text-secondary"
                    >
                        취소
                    </button>
                </div>
            </section>
        </div>,
        document.body
    );
}

/** 폼 안의 「고르기」 칸(누르면 시트). 값이 있으면 「변경」, 없으면 「선택」. disabled 면 흐리게 · 못 누름 */
export function PickerField({ label, labelId, valueText, placeholder, disabled = false, onOpen }) {
    return (
        <div className="flex flex-col gap-2">
            <span className="text-label" id={labelId}>
                {label}
            </span>
            <button
                type="button"
                aria-haspopup="dialog"
                aria-labelledby={labelId}
                aria-describedby={`${labelId}-value`}
                disabled={disabled}
                onClick={onOpen}
                className="flex h-12 items-center justify-between gap-2 rounded-card border border-hairline bg-surface px-3.5 text-left text-body disabled:opacity-40"
            >
                <span id={`${labelId}-value`} className={valueText ? 'text-text-primary' : 'text-text-tertiary'}>
                    {valueText ?? placeholder}
                </span>
                <span aria-hidden="true" className="text-[13px] font-semibold text-brand">
                    {valueText ? '변경' : '선택'}
                </span>
            </button>
        </div>
    );
}
